import React, { useState, useEffect } from 'react';
import { Download, RefreshCw, X, Smartphone, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

// Check if app is installed as PWA
const isPWAInstalled = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         window.navigator.standalone === true ||
         document.referrer.includes('android-app://');
};

// Check if running on iOS
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// Check if running on Android
const isAndroid = () => {
  return /Android/.test(navigator.userAgent);
};

// Enhanced App Update Notification Component
export const UpdateNotification = ({ 
  show: externalShow, 
  updateInfo,
  onDismiss, 
  onUpdate,
  isForceUpdate = false 
}) => {
  const [internalShow, setInternalShow] = useState(false);
  
  // Use external show prop if provided, otherwise use internal state
  const show = externalShow !== undefined ? externalShow : internalShow;
  
  useEffect(() => {
    // Listen for service worker updates (fallback)
    if ('serviceWorker' in navigator && externalShow === undefined) {
      const handleMessage = (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          setInternalShow(true);
        }
      };
      
      navigator.serviceWorker.addEventListener('message', handleMessage);
      
      // Check for updates on mount
      navigator.serviceWorker.ready.then((registration) => {
        registration.update();
      });
      
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, [externalShow]);
  
  const handleUpdate = () => {
    if (onUpdate) {
      onUpdate();
    } else {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((registration) => {
          if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      }
      window.location.reload();
    }
  };
  
  const handleDismiss = () => {
    if (!isForceUpdate) {
      setInternalShow(false);
      onDismiss?.();
    }
  };
  
  if (!show) return null;
  
  return (
    <div className="fixed top-4 left-4 right-4 z-[100] animate-slideDown" data-testid="update-notification">
      <div className={`bg-gradient-to-r ${isForceUpdate ? 'from-red-600/90 to-red-500/70' : 'from-primary/90 to-primary/70'} backdrop-blur-lg rounded-xl p-4 shadow-2xl border border-white/10`}>
        <div className="flex items-center gap-3">
          <div className="bg-white/20 rounded-full p-2 animate-pulse">
            {isForceUpdate ? (
              <Sparkles className="w-6 h-6 text-white" />
            ) : (
              <RefreshCw className="w-6 h-6 text-white" />
            )}
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-white flex items-center gap-2">
              {isForceUpdate ? '⚠️ تحديث إجباري!' : '🎉 تحديث جديد متاح!'}
              {updateInfo?.version && (
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                  v{updateInfo.version}
                </span>
              )}
            </h4>
            <p className="text-sm text-white/80">
              {updateInfo?.release_notes || 'يرجى تحديث التطبيق للحصول على أحدث الميزات والإصلاحات'}
            </p>
          </div>
          <div className="flex gap-2">
            {!isForceUpdate && (
              <Button
                onClick={handleDismiss}
                size="sm"
                variant="ghost"
                className="text-white/80 hover:text-white hover:bg-white/10"
                data-testid="dismiss-update-btn"
              >
                <X className="w-5 h-5" />
              </Button>
            )}
            <Button
              onClick={handleUpdate}
              size="sm"
              className="bg-white text-primary hover:bg-white/90 font-bold"
              data-testid="apply-update-btn"
            >
              <RefreshCw className="w-4 h-4 me-1" />
              تحديث الآن
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Install PWA Prompt Component
export const InstallPrompt = ({ show, onClose }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(isPWAInstalled());
  
  useEffect(() => {
    // Check if already installed
    setIsInstalled(isPWAInstalled());
    
    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Listen for app installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      toast.success('تم تثبيت التطبيق بنجاح!');
    });
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);
  
  const handleInstall = async () => {
    if (deferredPrompt) {
      // Chrome/Android install
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('تم تثبيت التطبيق!');
      }
      setDeferredPrompt(null);
    } else if (isIOS()) {
      // Show iOS instructions
      toast.info(
        <div className="space-y-2">
          <p className="font-bold">لتثبيت التطبيق على iPhone:</p>
          <ol className="text-sm space-y-1">
            <li>1. اضغط على زر المشاركة <span className="inline-block">⬆️</span></li>
            <li>2. اختر "إضافة إلى الشاشة الرئيسية"</li>
            <li>3. اضغط "إضافة"</li>
          </ol>
        </div>,
        { duration: 10000 }
      );
    } else {
      // Fallback - go to homepage
      window.location.href = '/';
    }
    onClose?.();
  };
  
  const handleUpdate = () => {
    // Force update by reloading
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update().then(() => {
          window.location.reload();
        });
      });
    } else {
      window.location.reload();
    }
    toast.success('جاري التحديث...');
    onClose?.();
  };
  
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-card border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-6">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl overflow-hidden shadow-lg">
            <img src="/icon-192.png" alt="KE Gents Saloon" className="w-full h-full object-cover" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">
            {isInstalled ? 'تحديث التطبيق' : 'تحميل التطبيق'}
          </h3>
          <p className="text-muted-foreground text-sm">
            {isInstalled 
              ? 'تحقق من وجود تحديثات جديدة للتطبيق'
              : 'أضف التطبيق إلى شاشتك الرئيسية للوصول السريع'}
          </p>
        </div>
        
        {/* Platform Info */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isIOS() ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}`}>
            <Smartphone className="w-4 h-4" />
            <span className="text-sm">iPhone</span>
          </div>
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isAndroid() ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
            <Smartphone className="w-4 h-4" />
            <span className="text-sm">Android</span>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="space-y-3">
          {isInstalled ? (
            <Button
              onClick={handleUpdate}
              className="w-full bg-primary hover:bg-primary/90 h-12 text-lg"
            >
              <RefreshCw className="w-5 h-5 me-2" />
              تحديث التطبيق
            </Button>
          ) : (
            <Button
              onClick={handleInstall}
              className="w-full bg-primary hover:bg-primary/90 h-12 text-lg"
            >
              <Download className="w-5 h-5 me-2" />
              {isIOS() ? 'كيفية التثبيت' : 'تثبيت التطبيق'}
            </Button>
          )}
          
          <Button
            onClick={onClose}
            variant="outline"
            className="w-full border-white/10 h-10"
          >
            لاحقاً
          </Button>
        </div>
        
        {/* Version Info */}
        <p className="text-center text-xs text-muted-foreground mt-4">
          الإصدار 2.0.0
        </p>
      </div>
    </div>
  );
};

export default { UpdateNotification, InstallPrompt };
